import {useState, useRef} from "react";
import {useNavigate} from "react-router-dom";

import Page from "@/components/Page";
import Loading from "@/components/Loading";
import Breadcrumbs from "@/components/Breadcrumbs";
import MainActions from "@/components/MainActions";
import VLFSelectNew from "@/components/Input/SelectNew";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import {PlayerAPI} from "@/api/player";
import {parseKSportCSV} from "@/utils/gpsParser";
import {saveSession} from "@/utils/gpsSessions";
import {ERROR_OPERATION, SUCCESSFUL_OPERATION} from "@/utils/constants";
import {playerToOption} from "@/utils/functions";

import "./index.scss";

// ── Step indicators ───────────────────────────────────────────────────────────

const STEPS = ["Carica file", "Associa giocatori", "Salva"];

function StepBar({current}) {
	return (
		<div className="gps-import-steps">
			{STEPS.map((label, i) => (
				<div
					key={i}
					className={
						"gps-import-step" +
						(i === current ? " gps-import-step--active" : "") +
						(i < current ? " gps-import-step--done" : "")
					}
				>
					<div className="gps-import-step__dot">
						{i < current ? <CheckCircleIcon fontSize="small" /> : i + 1}
					</div>
					<span className="gps-import-step__label">{label}</span>
				</div>
			))}
		</div>
	);
}

// ── Main ──────────────────────────────────────────────────────────────────────

function GPSImportPage(props) {
	const {handleNotification} = props;
	const navigate = useNavigate();
	const fileRef = useRef();

	const [step, setStep] = useState(0);
	const [loading, setLoading] = useState(false);

	// Step 0
	const [parsed, setParsed] = useState(null); // {meta, rows}
	const [filename, setFilename] = useState("");

	// Step 1 — mapping: { csvName → playerId }
	const [players, setPlayers] = useState([]);    // [{value, label}] from API
	const [mapping, setMapping] = useState({});    // csvName → {value, label} option

	// ── File upload ───────────────────────────────────────────────────────────
	const handleFile = (e) => {
		const file = e.target.files?.[0];
		if (!file) return;
		setFilename(file.name);
		const reader = new FileReader();
		reader.onload = (evt) => {
			try {
				const result = parseKSportCSV(evt.target.result, file.name);
				if (!result.rows.length) {
					handleNotification(
						"Il file non contiene righe valide (colonna 'Calciatore' non trovata?).",
						"error",
					);
					return;
				}
				setParsed(result);
			} catch {
				handleNotification("Errore nel parsing del file CSV.", "error");
			}
		};
		reader.readAsText(file, "utf-8");
	};

	// ── Go to step 1: load players from API ──────────────────────────────────
	const goToMapping = async () => {
		setLoading(true);
		try {
			const data = await PlayerAPI.list({prospect: false});
			setPlayers(data.map((p) => playerToOption(p)));
			// Pre-populate mapping by fuzzy name match
			const initMap = {};
			for (const row of parsed.rows) {
				const csvNorm = (row.playerName ?? "").toLowerCase().trim();
				const match = data.find((p) => {
					const full =
						(p.firstName + " " + p.lastName).toLowerCase();
					const rev =
						(p.lastName + " " + p.firstName).toLowerCase();
					return full === csvNorm || rev === csvNorm;
				});
				if (match) {
					initMap[row.playerName] = playerToOption(match);
				}
			}
			setMapping(initMap);
			setStep(1);
		} catch {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	// ── Save session ──────────────────────────────────────────────────────────
	const handleSave = () => {
		const enrichedRows = parsed.rows.map((row) => ({
			...row,
			playerId: mapping[row.playerName]?.value ?? null,
			playerLabel: mapping[row.playerName]?.label ?? row.playerName,
		}));

		const session = {
			id: "gps_" + Date.now(),
			provider: "ksport",
			sessionType: parsed.meta.sessionType,
			date: parsed.meta.date,
			time: parsed.meta.time,
			label: parsed.meta.label,
			rows: enrichedRows,
			importedAt: new Date().toISOString(),
		};

		saveSession(session);
		handleNotification(SUCCESSFUL_OPERATION, "success");
		navigate("/gps/" + session.id);
	};

	// ── Breadcrumbs ───────────────────────────────────────────────────────────
	const breadcrumbsElements = [
		{label: "GPS", path: "/gps"},
		{label: "/"},
		{label: "Importa CSV"},
	];

	const mainActions =
		step === 0
			? [
					{
						icon: <ArrowBackIcon />,
						title: "Indietro",
						onClick: () => navigate("/gps"),
						color: "info",
					},
					{
						icon: <CheckCircleIcon />,
						title: "Continua",
						onClick: goToMapping,
						disabled: !parsed,
					},
			  ]
			: step === 1
			? [
					{
						icon: <ArrowBackIcon />,
						title: "Indietro",
						onClick: () => setStep(0),
						color: "info",
					},
					{
						icon: <SaveIcon />,
						title: "Salva",
						onClick: handleSave,
					},
			  ]
			: [];

	// ── Render ────────────────────────────────────────────────────────────────
	return (
		<Page className="gps-import-page">
			<Loading visible={loading} />
			<Breadcrumbs
				elements={breadcrumbsElements}
				actions={<MainActions actions={mainActions} />}
			/>

			<div className="gps-import-body">
				<StepBar current={step} />

				{/* ── Step 0: File upload ─────────────────────────────────── */}
				{step === 0 && (
					<div className="gps-import-upload">
						<div
							className={"gps-import-dropzone" + (parsed ? " gps-import-dropzone--ok" : "")}
							onClick={() => fileRef.current.click()}
						>
							{parsed ? (
								<>
									<CheckCircleIcon className="gps-import-dropzone__icon gps-import-dropzone__icon--ok" />
									<div className="gps-import-dropzone__title">{filename}</div>
									<div className="gps-import-dropzone__sub">
										{parsed.rows.length} giocatori · {parsed.meta.sessionType === "match" ? "Partita" : "Allenamento"}{" "}
										{parsed.meta.date ?? ""}
									</div>
								</>
							) : (
								<>
									<UploadFileIcon className="gps-import-dropzone__icon" />
									<div className="gps-import-dropzone__title">
										Clicca per selezionare un CSV K-Sport
									</div>
									<div className="gps-import-dropzone__sub">
										Formati supportati: .csv (UTF-8 o Latin-1)
									</div>
								</>
							)}
						</div>
						<input
							ref={fileRef}
							type="file"
							accept=".csv"
							style={{display: "none"}}
							onChange={handleFile}
						/>
					</div>
				)}

				{/* ── Step 1: Player mapping ──────────────────────────────── */}
				{step === 1 && parsed && (
					<div className="gps-import-mapping">
						<p className="gps-import-mapping__hint">
							Associa ogni nome nel CSV a un giocatore del gestionale. Le righe con
							corrispondenza automatica sono già pre-compilate.
						</p>
						<div className="gps-import-mapping__table">
							<div className="gps-import-mapping__header">
								<span>Nome nel CSV</span>
								<span>Giocatore nel gestionale</span>
							</div>
							{parsed.rows.map((row) => (
								<div
									key={row.playerName}
									className="gps-import-mapping__row"
								>
									<span className="gps-import-mapping__csv-name">
										{row.playerName}
									</span>
									<div className="gps-import-mapping__select">
										<VLFSelectNew
											options={players}
											sortKey="sortKey"
											value={mapping[row.playerName] ?? null}
											onChange={(opt) =>
												setMapping((prev) => ({
													...prev,
													[row.playerName]: opt,
												}))
											}
											placeholder="Scegli giocatore…"
											isClearable
										/>
									</div>
								</div>
							))}
						</div>
					</div>
				)}
			</div>
		</Page>
	);
}

export default GPSImportPage;
